import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import {
  deleteCollectorPhoto,
  updatePhotoStatus,
  listProjectPlans,
  getFotoReviewProject,
  addCollectorToPo,
  correctCollector,
  fetchKringen,
  approveMeerwerkToPo,
  dismissMeerwerk,
  type CorrectCollectorPayload,
  type AddToPoOptions,
  type ReviewMeerwerk,
} from '@/api/tasks';
import { MEERWERK_TYPE_LABELS, MEERWERK_UNIT_LABELS } from '@/api/meerwerken';
import { ApiError } from '@/api/errors';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { isPdfPhoto } from '@/utils/photoFiles';
import { GoedkeurenModal } from '@/components/foto/GoedkeurenModal';
import { MeerwerkGoedkeurenModal } from '@/components/foto/MeerwerkGoedkeurenModal';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';
import type { PhotoItem } from '@/types/odoo';
import { useKringSystems } from '@/stores/systems';
import { GeenPrijsLabel } from '@/components/ui/GeenPrijsLabel';

interface ReviewKring {
  kringnummer: number;
  system: string;
  legpatroon: number;
  length: number;
}

interface ReviewCollector {
  id: number;
  name: string;
  collectorPath: string;
  systeem: string | null;
  m2: number;
  kringen: ReviewKring[];
  photos: PhotoItem[];
  onPo: { id: number; name: string } | null;
  installateur: { id: number; name: string; isSubcontractor: boolean } | null;
  basisM2: number;
  tier: 'under200' | 'from200';
  tierScope: 'materiaallijst' | 'project';
  tierLabel: string;
  geleverd: {
    flagged: boolean;
    source: 'exact' | 'project_flagged' | 'none';
    confidence: 'high' | 'low';
    faseName: string | null;
    faseNote: string | null;
    hint: boolean;
  };
}

interface ReviewBlok {
  blok: string;
  verdieping: string;
  collectors: ReviewCollector[];
}

interface ReviewProjectData {
  projectId: number;
  projectName: string;
  blokken: ReviewBlok[];
  /** Pending meerwerken for this project, enriched with isSubcontractor. */
  meerwerken?: ReviewMeerwerk[];
}

export function FotoReviewProjectView() {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = Number(projectIdParam);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Carried from the overview dropdown so the access re-check uses the selected
  // PL, not the caller — otherwise opening a colleague's project 403s.
  const selection = searchParams.get('projectleider') ?? '';
  const overviewHref = selection ? `/foto-controle?projectleider=${encodeURIComponent(selection)}` : '/foto-controle';

  const [data, setData] = useState<ReviewProjectData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyCollectorId, setBusyCollectorId] = useState<number | null>(null);
  const [busyMeerwerkId, setBusyMeerwerkId] = useState<number | null>(null);
  const [lightbox, setLightbox] = useState<{ photos: PhotoItem[]; index: number } | null>(null);
  const [verlegOpen, setVerlegOpen] = useState(false);
  const [pinnedPlan, setPinnedPlan] = useState<PhotoItem | null>(null);
  const [plans, setPlans] = useState<PhotoItem[]>([]);
  // Parent-level notice for PO success/warnings/errors — survives card removal + refetch
  const [notice, setNotice] = useState<{ type: 'success' | 'warning' | 'error'; message: string } | null>(null);

  // Guards in-flight requests against project navigation: if projectId changes
  // while a fetch is pending, the resolution of that fetch must not overwrite
  // state for the new project.
  const activeProjectIdRef = useRef<number>(projectId);

  // Extracted fetch so it can be called both from useEffect and after corrections/409s.
  const reload = useCallback(() => {
    const requestProjectId = activeProjectIdRef.current;
    const isStale = () => activeProjectIdRef.current !== requestProjectId;
    setLoading(true);
    setError(null);
    getFotoReviewProject<ReviewProjectData>(requestProjectId, selection || undefined)
      .then(d => { if (!isStale()) setData(d); })
      .catch((err: Error) => { if (!isStale()) setError(err.message || 'Fout bij laden'); })
      .finally(() => { if (!isStale()) setLoading(false); });
  }, [selection]);

  useEffect(() => {
    activeProjectIdRef.current = projectId;
    // Drop everything project-scoped before fetching the new project so the
    // previous project's plans / pinned PDF / collector data can never leak.
    setData(null);
    setPlans([]);
    setPinnedPlan(null);
    setVerlegOpen(false);

    if (!Number.isFinite(projectId) || projectId <= 0) {
      setError('Ongeldig project');
      setLoading(false);
      return;
    }
    reload();
    // Load plans in parallel for auto-matching; ignore errors (button just stays disabled)
    listProjectPlans(projectId)
      .then(({ plans }) => { setPlans(plans); })
      .catch(() => { setPlans([]); });
  }, [projectId, selection, reload]);

  const totalCollectors = data ? data.blokken.reduce((sum, b) => sum + b.collectors.length, 0) : 0;
  const pendingMeerwerken = data?.meerwerken ?? [];

  const planMatches = useMemo(() => {
    const map = new Map<number, PhotoItem>();
    if (!data || plans.length === 0) return map;
    for (const blok of data.blokken) {
      for (const c of blok.collectors) {
        const match = matchPlan(plans, blok.blok, blok.verdieping, c.name, data.projectName);
        if (match) map.set(c.id, match);
      }
    }
    return map;
  }, [data, plans]);

  const removeCollector = useCallback((collectorId: number) => {
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        blokken: prev.blokken
          .map(b => ({ ...b, collectors: b.collectors.filter(c => c.id !== collectorId) }))
          .filter(b => b.collectors.length > 0),
      };
    });
  }, []);

  const removeMeerwerk = useCallback((meerwerkId: number) => {
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        meerwerken: (prev.meerwerken ?? []).filter(m => m.id !== meerwerkId),
      };
    });
  }, []);

  /**
   * Bill a meerwerk onto the OA's rolling PO. Called by MeerwerkGoedkeurenModal
   * via the row's onConfirm wrapper. Throws on API error (modal catches + shows
   * inline); on success removes the row and shows a parent notice.
   */
  const handleApproveMeerwerk = useCallback(async (
    id: number,
    name: string,
    opts: { unitPrice?: number },
  ): Promise<void> => {
    const result = await approveMeerwerkToPo(id, opts); // throws on error
    removeMeerwerk(id);
    // A 207 with partialFailures means the PO line WAS billed but the status-flip write failed —
    // the meerwerk is still 'pending' (re-billable). Never report this as a clean success: tell
    // the reviewer to fix it in Odoo and NOT re-approve (which would double-bill).
    if (result.partialFailures && result.partialFailures.length > 0) {
      setNotice({
        type: 'error',
        message: `Meerwerk "${name}" is op de PO gezet, maar de status kon niet worden bijgewerkt — werk dit handmatig bij in Odoo en keur het NIET opnieuw goed. ${result.partialFailures.map(p => p.fix).join(' ')}`,
      });
      return;
    }
    if (result.warnings && result.warnings.length > 0) {
      setNotice({ type: 'warning', message: `${name}: ${result.warnings.join(' · ')}` });
    } else {
      setNotice({ type: 'success', message: `Meerwerk "${name}" goedgekeurd en op PO gezet.` });
    }
  }, [removeMeerwerk]);

  /** Confirm → dismiss → remove + notice. */
  const handleDismissMeerwerk = useCallback(async (id: number, name: string) => {
    if (!confirm(`Meerwerk "${name}" verwerpen?`)) return;
    setBusyMeerwerkId(id);
    try {
      await dismissMeerwerk(id);
      removeMeerwerk(id);
      setNotice({ type: 'success', message: `Meerwerk "${name}" verworpen.` });
    } catch (err) {
      setNotice({ type: 'error', message: err instanceof Error ? err.message : 'Verwerpen mislukt' });
    } finally {
      setBusyMeerwerkId(null);
    }
  }, [removeMeerwerk]);

  const handleApproveNoOa = useCallback(async (collectorId: number, collectorName: string) => {
    setBusyCollectorId(collectorId);
    try {
      await updatePhotoStatus(collectorId, 'Fotos_Goedgekeurd');
      removeCollector(collectorId);
      setNotice({ type: 'success', message: `${collectorName} goedgekeurd.` });
    } catch (err) {
      setNotice({ type: 'error', message: err instanceof Error ? err.message : 'Goedkeuren mislukt' });
    } finally {
      setBusyCollectorId(null);
    }
  }, [removeCollector]);

  const handleAddToPo = useCallback(async (collectorId: number, collectorName: string, opts: AddToPoOptions): Promise<void> => {
    let result: Awaited<ReturnType<typeof addCollectorToPo>>;
    try {
      result = await addCollectorToPo(collectorId, opts);
    } catch (err) {
      // Stale-page safety net: the server rejects if the fase is flagged AND neither geplaatst nor
      // acknowledgedGeleverd was set. The modal only acknowledges when its grid already showed the
      // flag, so this fires precisely when the flag was set after the page loaded (or for a direct
      // API caller) — exactly the case the server re-resolve exists to catch.
      if (err instanceof ApiError && err.code === 'COLLECTOR_LIKELY_PREPLACED') {
        // err.body is the full parsed payload { ok, code, message, body: { faseName, faseNote } }.
        const faseName = (err.body as { body?: { faseName?: string } } | undefined)?.body?.faseName;
        const confirmed = window.confirm(
          `${err.message}${faseName ? ` (${faseName})` : ''}\n\nToch goedkeuren en op PO zetten?`,
        );
        if (!confirmed) throw err; // propagates to modal resultBanner
        // Retry with explicit acknowledgement.
        result = await addCollectorToPo(collectorId, { ...opts, acknowledgedGeleverd: true });
      } else {
        throw err;
      }
    }
    removeCollector(collectorId);
    if (result.warnings && result.warnings.length > 0) {
      setNotice({ type: 'warning', message: `${collectorName}: ${result.warnings.join(' · ')}` });
    } else {
      setNotice({ type: 'success', message: `${collectorName} goedgekeurd en op PO gezet.` });
    }
  }, [removeCollector]);

  // 409 already-on-PO: the parent owns the message (survives the card unmount the
  // refetch triggers — a message set inside the modal would vanish with it), then
  // refetch so the stale onPo badge updates.
  const handleAlreadyOnPo = useCallback((message: string) => {
    setNotice({ type: 'error', message });
    reload();
  }, [reload]);

  const handleDeletePhoto = useCallback(async (collectorId: number, key: string) => {
    if (!confirm('Foto verwijderen?')) return;
    try {
      const res = await deleteCollectorPhoto(collectorId, key);
      setData(prev => {
        if (!prev) return prev;
        const blokken = prev.blokken.map(b => ({
          ...b,
          collectors: b.collectors.map(c =>
            c.id === collectorId
              ? { ...c, photos: c.photos.filter(p => p.key !== key) }
              : c,
          ),
        }));
        // Drop empty collectors / blokken (status flipped to Geen_Fotos server-side)
        const cleaned = blokken
          .map(b => ({ ...b, collectors: b.collectors.filter(c => c.photos.length > 0) }))
          .filter(b => b.collectors.length > 0);
        return { ...prev, blokken: res.opDrukChanged ? cleaned : blokken };
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Verwijderen mislukt';
      alert(msg);
    }
  }, []);

  const isPinned = pinnedPlan !== null;

  return (
    <div className={isPinned ? 'flex items-start' : ''}>
      <div className={isPinned ? 'flex-1 min-w-0 p-4 lg:p-6' : 'max-w-5xl mx-auto p-4 sm:p-6'}>
      <div className="flex items-center gap-3 mb-2">
        <Link to={overviewHref} className="text-sm text-td-blue hover:underline flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Foto Controle
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 truncate">{data?.projectName ?? 'Laden…'}</h1>
          {!loading && data && (
            <p className="text-sm text-gray-500 mt-1">
              {totalCollectors === 0 && pendingMeerwerken.length === 0
                ? 'Niets te keuren'
                : [
                    totalCollectors > 0
                      ? `${totalCollectors} ${totalCollectors === 1 ? 'collector' : 'collectoren'} te keuren`
                      : null,
                    pendingMeerwerken.length > 0
                      ? `${pendingMeerwerken.length} ${pendingMeerwerken.length === 1 ? 'meerwerk' : 'meerwerken'} te keuren`
                      : null,
                  ].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/project/${projectId}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:border-td-blue/40 hover:shadow-sm rounded-lg text-sm font-medium text-gray-700 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Naar project
          </Link>
          <button
            type="button"
            onClick={() => setVerlegOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:border-td-blue/40 hover:shadow-sm rounded-lg text-sm font-medium text-gray-700 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {isPinned ? 'Ander verlegschema' : 'Verlegschema'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" />
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
      )}

      {notice && (
        <div
          role="alert"
          data-testid="parent-notice"
          className={`mb-4 flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            notice.type === 'warning'
              ? 'bg-amber-50 border-amber-200 text-amber-900'
              : notice.type === 'error'
              ? 'bg-red-50 border-red-200 text-red-900'
              : 'bg-green-50 border-green-200 text-green-900'
          }`}
        >
          <span>{notice.message}</span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="text-current opacity-60 hover:opacity-100 flex-shrink-0"
            aria-label="Sluiten"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {!loading && !error && data && totalCollectors === 0 && pendingMeerwerken.length === 0 && (
        <div className="text-center py-20 text-gray-400">
          <svg className="w-16 h-16 mx-auto mb-4 opacity-40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-lg font-medium">Klaar — alles is goedgekeurd</p>
          <button
            type="button"
            onClick={() => navigate(overviewHref)}
            className="mt-4 text-sm text-td-blue hover:underline"
          >
            Terug naar overzicht
          </button>
        </div>
      )}

      {!loading && !error && data && data.blokken.map(blok => (
        <section key={`${blok.blok}__${blok.verdieping}`} className="mb-8">
          <h2 className="sticky top-0 bg-gray-50 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 text-sm font-semibold text-gray-600 z-10 border-b border-gray-200">
            Blok {blok.blok} &middot; {blok.verdieping}
          </h2>
          <div className="space-y-4 mt-4">
            {blok.collectors.map(c => (
              <CollectorCard
                key={c.id}
                collector={c}
                blok={blok.blok}
                verdieping={blok.verdieping}
                busy={busyCollectorId === c.id}
                matchedPlan={planMatches.get(c.id) ?? null}
                isPinnedMatch={pinnedPlan !== null && planMatches.get(c.id)?.key === pinnedPlan.key}
                onPinPlan={(plan) => setPinnedPlan(plan)}
                onApproveNoOa={() => handleApproveNoOa(c.id, c.name)}
                onAddToPo={handleAddToPo}
                onAlreadyOnPo={handleAlreadyOnPo}
                onRefetch={reload}
                onDeletePhoto={(key) => handleDeletePhoto(c.id, key)}
                onOpenLightbox={(index) => setLightbox({ photos: c.photos.filter((p) => !isPdfPhoto(p)), index })}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Meerwerken panel — shown when there are pending meerwerken, regardless of
          whether there are still collectors to review (meerwerk-only projects). */}
      {!loading && !error && data && pendingMeerwerken.length > 0 && (
        <MeerwerkReviewSection
          meerwerken={pendingMeerwerken}
          busyMeerwerkId={busyMeerwerkId}
          onApproveToPo={handleApproveMeerwerk}
          onDismiss={handleDismissMeerwerk}
        />
      )}

      </div>

      {isPinned && pinnedPlan && (
        <aside className="hidden lg:flex w-1/2 xl:w-[55%] flex-col border-l border-gray-200 bg-gray-100 sticky top-0 h-screen">
          <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
            <div className="text-sm font-medium text-gray-700 truncate min-w-0" title={pinnedPlan.filename}>{pinnedPlan.filename}</div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <a
                href={pinnedPlan.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-td-blue hover:underline px-2 py-1"
              >
                Open in nieuw tabblad
              </a>
              <button
                type="button"
                onClick={() => setPinnedPlan(null)}
                className="text-gray-400 hover:text-gray-700 p-1"
                aria-label="Verlegschema sluiten"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <iframe
            src={pinnedPlan.url}
            title={pinnedPlan.filename}
            className="flex-1 w-full bg-white"
          />
        </aside>
      )}

      {lightbox && (
        <PhotoLightbox
          photos={lightbox.photos}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}

      {verlegOpen && (
        <VerlegschemaSlideOver
          projectId={projectId}
          pinnedKey={pinnedPlan?.key ?? null}
          onPin={(plan) => { setPinnedPlan(plan); setVerlegOpen(false); }}
          onClose={() => setVerlegOpen(false)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Meerwerken review section — shown below the blok/collector list.
   Groups pending meerwerken by subcontractor name and surfaces
   approve (→ PO) / dismiss per row.
   ───────────────────────────────────────────────────────── */

/** Dutch quantity/unit formatter — mirrors MeerwerkenSection.fmtQty but Dutch. */
function fmtMeerwerkQty(m: ReviewMeerwerk): string {
  const { quantity, unit } = m;
  if (!quantity) return '';
  if (unit === 'hours') return `${quantity} u`;
  if (unit === 'pieces') return `${quantity} st`;
  if (unit === 'km') return `${quantity} km`;
  if (unit === 'lump_sum') return 'vast bedrag';
  return String(quantity);
}

interface MeerwerkReviewSectionProps {
  meerwerken: ReviewMeerwerk[];
  busyMeerwerkId: number | null;
  onApproveToPo: (id: number, name: string, opts: { unitPrice?: number }) => Promise<void>;
  onDismiss: (id: number, name: string) => Promise<void>;
}

function MeerwerkReviewSection({
  meerwerken,
  busyMeerwerkId,
  onApproveToPo,
  onDismiss,
}: MeerwerkReviewSectionProps) {
  const [activeGoedkeurenId, setActiveGoedkeurenId] = useState<number | null>(null);

  // Group by subcontractor_name (null → "Onbekend").
  const groups = useMemo(() => {
    const map = new Map<string, ReviewMeerwerk[]>();
    for (const m of meerwerken) {
      const key = m.subcontractor_name ?? 'Onbekend';
      const existing = map.get(key) ?? [];
      existing.push(m);
      map.set(key, existing);
    }
    return map;
  }, [meerwerken]);

  const activeMeerwerk = activeGoedkeurenId !== null
    ? (meerwerken.find(m => m.id === activeGoedkeurenId) ?? null)
    : null;

  return (
    <section className="mt-8" data-testid="meerwerken-section">
      <h2 className="sticky top-0 bg-gray-50 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 text-sm font-semibold text-gray-600 z-10 border-b border-gray-200">
        Meerwerken te keuren
      </h2>
      <div className="space-y-6 mt-4">
        {[...groups.entries()].map(([oaName, items]) => (
          <div key={oaName}>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 px-1">
              {oaName}
            </h3>
            <div className="space-y-2">
              {items.map(m => (
                <MeerwerkReviewRow
                  key={m.id}
                  meerwerk={m}
                  busy={busyMeerwerkId === m.id}
                  onGoedkeuren={() => setActiveGoedkeurenId(m.id)}
                  onDismiss={() => onDismiss(m.id, m.name)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {activeMeerwerk && (
        <MeerwerkGoedkeurenModal
          meerwerk={activeMeerwerk}
          onCancel={() => setActiveGoedkeurenId(null)}
          onConfirm={async (opts) => {
            await onApproveToPo(activeMeerwerk.id, activeMeerwerk.name, opts);
            setActiveGoedkeurenId(null);
          }}
        />
      )}
    </section>
  );
}

interface MeerwerkReviewRowProps {
  meerwerk: ReviewMeerwerk;
  busy: boolean;
  onGoedkeuren: () => void;
  onDismiss: () => void;
}

function MeerwerkReviewRow({ meerwerk, busy, onGoedkeuren, onDismiss }: MeerwerkReviewRowProps) {
  const isKm = meerwerk.type === 'kilometervergoeding';
  const typeLabel = MEERWERK_TYPE_LABELS[meerwerk.type] ?? meerwerk.type;
  // null = no price (a km line the server would refuse).
  const price = meerwerk.effective_unit_price;
  // Red when werkuren/overig has no price set.
  const isUnpriced = !isKm && (price === null || price <= 0);
  const unitLabel = meerwerk.unit
    ? (MEERWERK_UNIT_LABELS[meerwerk.unit] ?? meerwerk.unit).toLowerCase()
    : null;
  const qtyStr = fmtMeerwerkQty(meerwerk);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      {/* Top: type badge + name + qty */}
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-td-blue/10 text-td-blue shrink-0">
          {typeLabel}
        </span>
        <span className="text-sm font-semibold text-gray-900 flex-1 min-w-0">{meerwerk.name}</span>
        {qtyStr && (
          <span className="text-sm text-gray-700 whitespace-nowrap shrink-0">{qtyStr}</span>
        )}
      </div>

      {/* Meta row: date · task · price · note */}
      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap">
        {meerwerk.date && <span>{meerwerk.date}</span>}{meerwerk.task_name && <span>· {meerwerk.task_name}{meerwerk.task_is_interventie && <InterventieBadge className="ml-1.5 align-middle" />}</span>}
        {/* Price: red for unpriced non-km, normal otherwise */}
        {isUnpriced ? (
          <span className="font-semibold text-red-500">· Geen prijs</span>
        ) : price === null ? (
          <GeenPrijsLabel reason={meerwerk.price_unavailable_reason} className="basis-full" />
        ) : price > 0 && unitLabel ? (
          <span>· € {price.toFixed(2)} / {unitLabel}</span>
        ) : null}
      </div>
      {meerwerk.note && <p className="text-xs text-gray-400 mt-1">{meerwerk.note}</p>}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 mt-3">
        {!meerwerk.isSubcontractor && (
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-md mr-auto">
            Eigen werknemer — geen PO
          </span>
        )}
        <button
          type="button"
          onClick={onDismiss}
          disabled={busy}
          className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-200 hover:border-red-300 text-gray-600 hover:text-red-600 rounded-md disabled:opacity-50 transition"
        >
          {busy ? 'Bezig…' : 'Verwerpen'}
        </button>
        {meerwerk.isSubcontractor && (
          <button
            type="button"
            onClick={onGoedkeuren}
            disabled={busy}
            className="px-3 py-1.5 text-sm font-semibold bg-td-blue hover:bg-td-blue/90 text-white rounded-md disabled:opacity-50 transition"
          >
            Goedkeuren & op PO
          </button>
        )}
      </div>
    </div>
  );
}

interface CollectorCardProps {
  collector: ReviewCollector;
  blok: string;
  verdieping: string;
  busy: boolean;
  matchedPlan: PhotoItem | null;
  isPinnedMatch: boolean;
  onPinPlan: (plan: PhotoItem) => void;
  /** Plain approve — used when installateur is null. */
  onApproveNoOa: () => void;
  /** Bill onto PO + flip status. Parent handles notice display. Throws ApiError on failure. */
  onAddToPo: (collectorId: number, collectorName: string, opts: AddToPoOptions) => Promise<void>;
  /** 409 already-on-PO: parent shows the message (survives card unmount) + refetches. */
  onAlreadyOnPo: (message: string) => void;
  /** Re-fetch the project (e.g. after a successful correction). */
  onRefetch: () => void;
  onDeletePhoto: (key: string) => void;
  onOpenLightbox: (index: number) => void;
}

function CollectorCard({
  collector, blok, verdieping, busy, matchedPlan, isPinnedMatch,
  onPinPlan, onApproveNoOa, onAddToPo, onAlreadyOnPo, onRefetch, onDeletePhoto, onOpenLightbox,
}: CollectorCardProps) {
  const [showGoedkeuren, setShowGoedkeuren] = useState(false);
  const [showBewerken, setShowBewerken] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="font-semibold text-gray-900 min-w-0 flex-1 truncate" title={`Blok ${blok} / ${verdieping} / ${collector.name}`}>
          Blok {blok} / {verdieping} / {collector.name}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs min-w-0">
          {/* Onderaannemer badge */}
          {collector.installateur ? (
            <span className="max-w-[10rem] truncate px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium" title={collector.installateur.name}>
              {collector.installateur.name}
            </span>
          ) : null}
          {/* PO-status pill */}
          {collector.onPo ? (
            <span
              className="px-2 py-0.5 rounded-full font-medium bg-td-green text-white"
              data-testid="po-pill-on"
            >
              Op PO {collector.onPo.name}
            </span>
          ) : (
            <span
              className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-medium"
              data-testid="po-pill-off"
            >
              Nog niet op PO
            </span>
          )}
          {collector.systeem && (
            <span className="px-2 py-0.5 bg-td-blue/10 text-td-blue rounded-full font-medium capitalize">
              {collector.systeem}
            </span>
          )}
          {collector.m2 > 0 && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
              {collector.m2.toFixed(1)} m²
            </span>
          )}
          {/* Collectoren geleverd signals */}
          {collector.geleverd?.flagged ? (
            <span
              className="px-2 py-0.5 rounded-full font-medium bg-amber-500 text-white"
              title={`Deze fase is gemarkeerd als "Collectoren geleverd"${collector.geleverd.faseName ? ` (${collector.geleverd.faseName})` : ''} — mogelijk al door de klant geplaatst.${collector.geleverd.faseNote ? `\n${collector.geleverd.faseNote}` : ''}`}
            >
              Collectoren geleverd
            </span>
          ) : (collector.geleverd?.source === 'project_flagged' || collector.geleverd?.hint) ? (
            <span
              className="text-gray-400 text-xs italic"
              title={`Mogelijk al geplaatst — controleer${collector.geleverd.faseName ? ` (${collector.geleverd.faseName})` : ''}${collector.geleverd.faseNote ? `\n${collector.geleverd.faseNote}` : ''}`}
            >
              Mogelijk geplaatst?
            </span>
          ) : null}
          <span className="text-gray-400">
            {collector.photos.length} {collector.photos.length === 1 ? 'foto' : "foto's"}
          </span>
          {matchedPlan ? (
            <button
              type="button"
              onClick={() => onPinPlan(matchedPlan)}
              title={matchedPlan.filename}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition ${
                isPinnedMatch
                  ? 'bg-td-blue text-white'
                  : 'bg-td-blue/10 text-td-blue hover:bg-td-blue/20'
              }`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {isPinnedMatch ? 'Verlegschema getoond' : 'Toon verlegschema'}
            </button>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 text-xs">
              Geen verlegschema gevonden
            </span>
          )}
        </div>
      </div>

      {/* Read-only kringen summary — hidden while Bewerken is open (the editor below
          shows the same kringen, editable, so showing both is confusing duplication). */}
      {!showBewerken && collector.kringen.length > 0 && (
        <div className="mb-3 overflow-x-auto">
          <table className="text-xs w-full">
            <thead>
              <tr className="text-gray-500 border-b border-gray-100">
                <th className="text-left font-medium py-1 pr-3">Kring</th>
                <th className="text-left font-medium py-1 pr-3">Systeem</th>
                <th className="text-right font-medium py-1 pr-3">LP</th>
                <th className="text-right font-medium py-1">Lengte</th>
              </tr>
            </thead>
            <tbody>
              {collector.kringen.map(k => (
                <tr key={k.kringnummer} className="border-b border-gray-50 last:border-0">
                  <td className="py-1 pr-3 font-medium text-gray-900">{k.kringnummer}</td>
                  <td className="py-1 pr-3 text-gray-700 capitalize">{k.system || '-'}</td>
                  <td className="py-1 pr-3 text-right text-gray-700">{k.legpatroon || '-'}</td>
                  <td className="py-1 text-right text-gray-700">{k.length ? `${k.length} m` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {collector.photos.length === 0 ? (
        <div className="text-sm text-gray-400 italic py-6 text-center">Geen foto's</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-4">
          {collector.photos.map((photo) => (
            <div key={photo.key} className="relative group aspect-square bg-gray-100 rounded-md overflow-hidden">
              <PhotoGridThumb
                photo={photo}
                tileSizeClass="absolute inset-0 w-full h-full"
                imgClassName="absolute inset-0 w-full h-full object-cover"
                openLabel="Foto vergroten"
                onOpen={() => onOpenLightbox(
                  collector.photos.filter((p) => !isPdfPhoto(p)).findIndex((p) => p.key === photo.key),
                )}
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDeletePhoto(photo.key); }}
                className="absolute top-1 right-1 bg-white/90 hover:bg-red-50 text-red-600 rounded-full p-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition shadow"
                aria-label="Foto verwijderen"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setShowBewerken(true)}
          disabled={busy}
          className="px-3 py-2 bg-white border border-gray-200 hover:border-td-blue/40 text-gray-700 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          Bewerken
        </button>
        <button
          type="button"
          onClick={() => setShowGoedkeuren(true)}
          disabled={busy || collector.photos.length === 0}
          className="px-4 py-2 bg-td-blue hover:bg-td-blue/90 text-white text-sm font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {busy ? 'Bezig…' : 'Goedkeuren'}
        </button>
      </div>

      {showBewerken && (
        <CollectorCorrectPanel
          collector={collector}
          onClose={() => setShowBewerken(false)}
          onSaved={() => { setShowBewerken(false); onRefetch(); }}
        />
      )}

      {showGoedkeuren && (
        <GoedkeurenModal
          collector={collector}
          onCancel={() => setShowGoedkeuren(false)}
          onApproveNoOa={() => { setShowGoedkeuren(false); onApproveNoOa(); }}
          onAddToPo={async (opts) => {
            // Acknowledge ONLY when this grid already knew the fase was flagged (the reviewer saw the
            // warning + preselected minprijs). If the grid is stale-false — the flag was set after the
            // page loaded — we deliberately omit the ack so the server re-resolves fresh, returns 409,
            // and the window.confirm safety net (handleAddToPo) fires. Never blanket-acknowledge on a
            // money path.
            await onAddToPo(collector.id, collector.name, {
              ...opts,
              acknowledgedGeleverd: !!collector.geleverd?.flagged,
            });
            setShowGoedkeuren(false);
          }}
          onAlreadyOnPo={onAlreadyOnPo}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Bewerken inline panel — lean correction editor.
   Only edits what `correctCollector` can persist:
   systeem, m², and per-existing-kring x_system + x_legpatroon.
   ───────────────────────────────────────────────────────── */
interface CollectorCorrectPanelProps {
  collector: ReviewCollector;
  onClose: () => void;
  onSaved: () => void;
}

interface LocalKringEdit {
  kringId: number;
  kringnummer: number;
  x_system: string;
  x_legpatroon: number;
}

function CollectorCorrectPanel({ collector, onClose, onSaved }: CollectorCorrectPanelProps) {
  // Both selects offer the KRING roster (Odoo-defined) — unchanged from the static
  // list this replaced, which also left the collector-only "meerdere systemen" out.
  const kringSystems = useKringSystems();
  const [systeem, setSysteem] = useState(collector.systeem ?? 'tacker');
  const [m2, setM2] = useState(String(collector.m2 || ''));
  // kringen are seeded from the review data (no IDs) and enriched with Odoo IDs
  // once fetchKringen resolves. Edits are blocked until IDs are available.
  const [kringen, setKringen] = useState<LocalKringEdit[]>(
    collector.kringen.map(k => ({
      kringId: 0, // placeholder — replaced once fetchKringen resolves
      kringnummer: k.kringnummer,
      x_system: k.system || '',
      x_legpatroon: k.legpatroon || 0,
    })),
  );
  const [kringenLoaded, setKringenLoaded] = useState(false);
  const [kringError, setKringError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load real Odoo kring IDs on mount so correctCollector receives valid kringIds.
  // Any kring whose id stays 0 (fetch failed, or no match) is LOCKED below — its
  // edits would be silently dropped by the kringId!==0 save filter otherwise.
  useEffect(() => {
    fetchKringen(collector.id)
      .then(({ kringen: odooKringen }) => {
        setKringen(prev => prev.map(local => {
          const odoo = odooKringen.find(k => k.x_kringnummer === local.kringnummer);
          return odoo ? { ...local, kringId: odoo.id } : local;
        }));
        setKringenLoaded(true);
      })
      .catch(() => {
        // Fetch failed: systeem/m² stay editable, but kring rows lock (kringId=0).
        setKringError(true);
        setKringenLoaded(true);
      });
  }, [collector.id]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: CorrectCollectorPayload = {};

      if (systeem !== (collector.systeem ?? 'tacker')) {
        payload.systeem = systeem;
      }

      const parsedM2 = parseFloat(m2);
      if (!isNaN(parsedM2) && parsedM2 !== collector.m2) {
        payload.collector_m2 = parsedM2;
      }

      const kringenChanges = kringen
        .filter(k => k.kringId !== 0)  // skip unresolved IDs (locked in the UI)
        .map(k => {
          // Match the original by kringnummer, NOT list position — a filtered-out
          // unresolved kring would otherwise shift the index and diff against the
          // wrong baseline (a money-adjacent write).
          const orig = collector.kringen.find(o => o.kringnummer === k.kringnummer);
          const update: { kringId: number; x_system?: string; x_legpatroon?: number } = { kringId: k.kringId };
          // Never send an empty system — clearing is forbidden (a kring needs a system to bill).
          if (orig && k.x_system && k.x_system !== (orig.system || '')) update.x_system = k.x_system;
          if (orig && k.x_legpatroon !== (orig.legpatroon || 0)) update.x_legpatroon = k.x_legpatroon;
          return update;
        })
        .filter(u => u.x_system !== undefined || u.x_legpatroon !== undefined);

      if (kringenChanges.length > 0) {
        payload.kringen = kringenChanges;
      }

      // Nothing changed — just close
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }

      await correctCollector(collector.id, payload);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ALREADY_ON_PO') {
        setError('Deze collector staat al op een PO en kan niet meer worden aangepast.');
      } else {
        setError(err instanceof Error ? err.message : 'Opslaan mislukt');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
      <h4 className="text-sm font-bold text-gray-900 mb-3">Collector bewerken</h4>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label htmlFor={`rev-systeem-${collector.id}`} className="block text-xs font-semibold text-gray-500 mb-1">
            Systeem
          </label>
          <select
            id={`rev-systeem-${collector.id}`}
            value={systeem}
            onChange={(e) => setSysteem(e.target.value)}
            disabled={saving}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue bg-white disabled:opacity-60"
          >
            {kringSystems.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={`rev-m2-${collector.id}`} className="block text-xs font-semibold text-gray-500 mb-1">
            m²
          </label>
          <input
            id={`rev-m2-${collector.id}`}
            type="number"
            step="0.1"
            min="0"
            max="10000"
            value={m2}
            onChange={(e) => setM2(e.target.value)}
            disabled={saving}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue disabled:opacity-60"
          />
        </div>
      </div>

      {kringen.length > 0 && (
        <div className="mb-4">
          <h5 className="text-xs font-semibold text-gray-500 mb-2">Kringen</h5>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                <th className="py-1 pr-2">#</th>
                <th className="py-1 pr-2">Systeem</th>
                <th className="py-1">LP</th>
              </tr>
            </thead>
            <tbody>
              {kringen.map((k, idx) => (
                <tr key={k.kringnummer} className="border-t border-gray-100">
                  <td className="py-1 pr-2 text-gray-700">{k.kringnummer}</td>
                  <td className="py-1 pr-2">
                    <select
                      value={k.x_system}
                      disabled={saving || k.kringId === 0}
                      onChange={(e) => {
                        const val = e.target.value;
                        setKringen(prev => prev.map((kr, i) => i === idx ? { ...kr, x_system: val } : kr));
                      }}
                      className="w-full rounded border border-gray-200 px-1 py-0.5 text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-td-blue/40 disabled:opacity-60 bg-white"
                      aria-label={`Systeem kring ${k.kringnummer}`}
                    >
                      <option value="" disabled>-</option>
                      {kringSystems.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="py-1">
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={k.x_legpatroon || ''}
                      disabled={saving || k.kringId === 0}
                      onChange={(e) => {
                        const val = e.target.value === '' ? 0 : Number(e.target.value);
                        setKringen(prev => prev.map((kr, i) => i === idx ? { ...kr, x_legpatroon: val } : kr));
                      }}
                      className="w-16 rounded border border-gray-200 px-1 py-0.5 text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-td-blue/40 disabled:opacity-60"
                      aria-label={`LP kring ${k.kringnummer}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {kringError && (
        <div className="mb-3 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
          Kringen konden niet geladen worden — alleen systeem en m² kunnen aangepast worden.
        </div>
      )}

      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !kringenLoaded}
          className="px-4 py-2 text-sm font-semibold bg-td-blue text-white rounded-lg hover:bg-td-blue/90 transition disabled:opacity-50"
        >
          {saving ? 'Opslaan…' : 'Opslaan'}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="px-4 py-2 text-sm font-semibold text-gray-500 hover:text-gray-900 transition disabled:opacity-50"
        >
          Annuleren
        </button>
      </div>
    </div>
  );
}

// GoedkeurenModal is now in @/components/foto/GoedkeurenModal.tsx (shared with the dashboard panel).

interface VerlegschemaSlideOverProps {
  projectId: number;
  pinnedKey: string | null;
  onPin: (plan: PhotoItem) => void;
  onClose: () => void;
}

function VerlegschemaSlideOver({ projectId, pinnedKey, onPin, onClose }: VerlegschemaSlideOverProps) {
  const [plans, setPlans] = useState<PhotoItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listProjectPlans(projectId)
      .then(({ plans }) => setPlans(plans))
      .catch((err: Error) => setError(err.message || 'Fout bij laden'));
  }, [projectId]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        onClick={onClose}
        aria-label="Sluiten"
        className="absolute inset-0 bg-black/40"
      />
      <div className="relative bg-white w-full sm:w-[80vw] sm:max-w-2xl h-full shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Verlegschema</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
          )}
          {!error && plans === null && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" />
            </div>
          )}
          {plans !== null && plans.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">
              Geen verlegschema's opgeslagen voor dit project
            </p>
          )}
          {plans !== null && plans.length > 0 && (
            <ul className="space-y-2">
              {plans.map(p => {
                const isPinned = p.key === pinnedKey;
                return (
                  <li
                    key={p.key}
                    className={`flex items-center gap-3 px-3 py-2 border rounded-md text-sm transition ${
                      isPinned
                        ? 'bg-td-blue/5 border-td-blue/40'
                        : 'bg-gray-50 border-gray-200 hover:border-td-blue/40'
                    }`}
                  >
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h6.586A2 2 0 0114 2.586L17.414 6A2 2 0 0118 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                    </svg>
                    <span className="truncate flex-1 min-w-0 text-gray-700" title={p.filename}>{p.filename}</span>
                    <button
                      type="button"
                      onClick={() => onPin(p)}
                      className="text-xs font-medium px-2 py-1 rounded bg-td-blue text-white hover:bg-td-blue/90"
                    >
                      {isPinned ? 'Aangepind' : 'Toon ernaast'}
                    </button>
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-td-blue hover:underline font-medium px-1"
                    >
                      Open
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// Strip the Date.now()-style 13-digit timestamp prefix added when plans are
// saved (`{projectName}/plans/{Date.now()}-{filename}`). Without this, the
// timestamp digits match any blok/verdieping number and every plan scores
// equal — first one always wins.
function normalizePlanFilename(filename: string): string {
  return filename
    .toLowerCase()
    .replace(/\.pdf$/, '')
    .replace(/^\d{10,}-/, '')      // Date.now() ms prefix
    .replace(/^\d{8}-\d{4,6}/, '') // alt ddmmyyyy-hhmmss prefix
    .replace(/[\s_/+()]+/g, '-')
    .replace(/-+/g, '-');
}

// Token-match using word boundaries so "blok-1" doesn't accidentally match
// "blok-10" and a bare digit doesn't match every numeric run.
function tokenMatches(haystack: string, token: string): boolean {
  if (!token) return false;
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i').test(haystack);
}

function matchPlan(
  plans: PhotoItem[],
  blok: string,
  verdieping: string,
  collectorName: string,
  projectName: string,
): PhotoItem | null {
  if (plans.length === 0) return null;

  const blokTokens = expandBlokTokens(blok);
  const verdiepingTokens = expandVerdiepingTokens(verdieping);
  const buildingTokens = projectName
    .toLowerCase()
    .split(/[\s_/\-+()]+/)
    .filter(t => t.length >= 4 && !GENERIC_PROJECT_WORDS.has(t));
  const collectorTokens = collectorName
    .toLowerCase()
    .split(/[\s_/\-+()]+/)
    .filter(t => t.length >= 3);

  let best: { plan: PhotoItem; score: number } | null = null;
  for (const plan of plans) {
    const haystack = normalizePlanFilename(plan.filename);
    let score = 0;
    if (blokTokens.some(t => tokenMatches(haystack, t))) score += 3;
    if (verdiepingTokens.some(t => tokenMatches(haystack, t))) score += 3;
    if (buildingTokens.some(t => tokenMatches(haystack, t))) score += 1;
    if (collectorTokens.some(t => tokenMatches(haystack, t))) score += 1;
    if (score === 0) continue;
    if (!best || score > best.score || (score === best.score && haystack.length < normalizePlanFilename(best.plan.filename).length)) {
      best = { plan, score };
    }
  }
  // Require at least one strong (blok or verdieping) hit
  return best && best.score >= 3 ? best.plan : null;
}

const GENERIC_PROJECT_WORDS = new Set([
  'project', 'residentie', 'appartement', 'appartementen', 'gebouw', 'woning',
  'woningen', 'huis', 'villa', 'kantoor', 'thermoduct', 'tdt', 'thd',
]);

function expandBlokTokens(blok: string): string[] {
  const raw = blok.toLowerCase().trim();
  if (!raw) return [];
  const out = new Set<string>();
  const numMatch = raw.match(/(\d+)/);
  if (numMatch) {
    const n = numMatch[1];
    const nNoZero = String(parseInt(n, 10));
    // Qualified forms only — no bare "1" since plan filenames may have other digits
    out.add(`blok-${n}`);
    out.add(`blok-${nNoZero}`);
    out.add(`blok${nNoZero}`);
    out.add(`b${nNoZero}`);
  }
  if (/^[a-z]$/.test(raw)) {
    out.add(`blok-${raw}`);
    out.add(`blok${raw}`);
  }
  // If blok name has a meaningful word (e.g. "Hoofdgebouw"), match it whole
  if (/^[a-z]{4,}$/.test(raw)) {
    out.add(raw);
  }
  return [...out];
}

function expandVerdiepingTokens(verdieping: string): string[] {
  const raw = verdieping.toLowerCase().trim();
  if (!raw) return [];
  const out = new Set<string>();
  const numMatch = raw.match(/(\d+)/);
  if (numMatch) {
    const n = numMatch[1];
    const nNoZero = String(parseInt(n, 10));
    // Qualified forms — bare numbers are too noisy, would match anywhere
    out.add(`+${n}`);
    out.add(`+${nNoZero}`);
    out.add(`v${nNoZero}`);
    out.add(`${nNoZero}v`);
    out.add(`verd-${nNoZero}`);
    out.add(`verdieping-${nNoZero}`);
    out.add(`verd${nNoZero}`);
    // The padded form is fine if it's not a 1-digit bare number
    if (n.length >= 2) out.add(n);
  }
  if (raw.includes('glv') || raw.includes('gelijk')) {
    out.add('glv');
    out.add('gelijkvloers');
    out.add('gv');
  }
  return [...out];
}
